import axios from 'axios'
import { authService } from '@/services/api'

const apiClient = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
})

// Refresh lock - stores the pending refresh promise to prevent race conditions
// When multiple requests get 401 simultaneously, only the first triggers a refresh
// Subsequent requests wait for this promise and share the result
let refreshPromise: Promise<boolean> | null = null

// Request interceptor - add auth token and active profile header
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    const profileId = localStorage.getItem('current_profile_id')
    if (profileId) {
      config.headers['X-Profile-Id'] = profileId
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor - handle 401 and token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Public endpoints that shouldn't trigger login redirect or token refresh
    const PUBLIC_ENDPOINTS = [
      '/profiles/',
      '/api/v1/profiles/',
      '/auth/login',
      '/auth/register',
      '/auth/forgot-password',
      '/auth/reset-password',
      '/auth/refresh',
      '/social/',
    ]
    const isPublicEndpoint = PUBLIC_ENDPOINTS.some(ep => originalRequest.url?.includes(ep))

    if (error.response?.status === 401 && !originalRequest._retry && !isPublicEndpoint) {
      originalRequest._retry = true

      try {
        // If refresh is already in progress, wait for it instead of spawning a new request
        if (!refreshPromise) {
          refreshPromise = authService.refreshToken().finally(() => {
            // Clear the lock once refresh completes (success or failure)
            refreshPromise = null
          })
        }

        const refreshed = await refreshPromise

        if (refreshed) {
          // Retry the original request with new token
          const token = localStorage.getItem('access_token')
          originalRequest.headers.Authorization = `Bearer ${token}`
          return apiClient(originalRequest)
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }

      // Refresh returned falsy or didn't complete, redirect to login
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      window.location.href = '/login'
    }

    return Promise.reject(error)
  }
)

export default apiClient
